import classNames from "classnames"
import React, { useEffect, useRef } from "react"

import { Experiment } from "../../../../constants/experiments"
import { Color } from "../../../../constants/V2/color"
import { useIsExperiment } from "../../../../hooks/experiments"
import AddToCartButton from "../../../elements/V2/Buttons/AddToCartButton"
import PillButton from "../../../elements/V2/Buttons/PillButton"
import TextButton from "../../../elements/V2/Buttons/TextButton"
import Callout from "../../../elements/V2/Callout"
import GatsbyStoryblokImage from "../../../elements/V2/GatsbyStoryblokImage"
import NumberDisplay from "../../../elements/V2/NumberDisplay"
import Section from "../../../elements/V2/Section"
import Typography from "../../../elements/V2/Typography"
import HeroLayout from "../HeroLayout"

import Video from "@components/elements/V2/Video"
import { PurchasableItem } from "@hooks/V2/storyblok"
import { useElementObserver } from "@hooks/V2/useElementObserver"
import { usePageLayoutState } from "@utils/layout"
import { getUrlFromStoryblokLink } from "@utils/storyblok"
import { getBackgroundColorClass, getAccentColor } from "@utils/V2/color"

type Props = {
  headingText: string
  headingSize?: "display1" | "h1" | "h2" | "h3" | "h4" | "h5"
  headingMobileAlignment?: "left" | "center"
  bodyText: string
  priceValue?: string | number
  priceSuperscriptText?: string
  priceSidescriptText?: string
  priceSubscriptText?: string
  backgroundColor: Color
  primaryButtonText?: string
  primaryButtonLink?: string
  primaryButtonMobileLink?: string
  primaryButtonBorderColor: Color //TODO this should be primaryButtonColor
  secondaryButtonText: string
  secondaryButtonLink: string
  imageUrl: string
  imageAlt: string
  videoUrl?: string
  isPurchasable?: boolean
  purchasableItem: PurchasableItem
  iconUrl?: string
  callout?: Storyblok.Callout
  reasonNotAvailable?: string
  waitlistLink?: string
  hasScrollArrow?: boolean
  secondaryTrackingEvent: string
  secondaryTrackingEventKey?: string
  secondaryTrackingEventValue?: string
} & Partial<Storyblok.CTATrackingEvent>

const HalfHero = ({
  headingText,
  headingSize = "h3",
  headingMobileAlignment = "left",
  bodyText,
  priceValue,
  priceSubscriptText,
  priceSidescriptText,
  priceSuperscriptText,
  backgroundColor,
  primaryButtonText,
  primaryButtonLink,
  primaryButtonMobileLink,
  primaryButtonBorderColor,
  secondaryButtonText,
  secondaryButtonLink,
  imageUrl,
  imageAlt,
  videoUrl,
  iconUrl,
  isPurchasable = false,
  purchasableItem,
  callout,
  trackingEvent,
  trackingEventKey,
  trackingEventValue,
  reasonNotAvailable,
  waitlistLink,
  hasScrollArrow = false,
  secondaryTrackingEvent,
  secondaryTrackingEventKey,
  secondaryTrackingEventValue,
  ...props
}: Props) => {
  const isProductHeroExperiment = useIsExperiment(Experiment.PRODUCT_HERO_CTA)

  const textContainerRef = useRef(null)
  const { isVisible: isCtaVisible, startObserving } =
    useElementObserver(textContainerRef)
  const accentColor = getAccentColor(backgroundColor)

  const { setHeroCTAPurchasableItem, setHeroCTAVisible, hasAnchorNavigation } =
    usePageLayoutState()

  const renderPrimaryCTA = (): React.ReactNode => {
    return (
      <>
        {isPurchasable && !isProductHeroExperiment ? (
          <AddToCartButton
            style="solid"
            purchasableItem={purchasableItem}
            buttonColor={primaryButtonBorderColor}
            reasonNotAvailable={reasonNotAvailable}
            waitlistLink={waitlistLink}
          />
        ) : primaryButtonText && primaryButtonLink ? (
          <PillButton
            size="large"
            style="solid"
            text={primaryButtonText}
            color={primaryButtonBorderColor}
            linkUrl={primaryButtonLink}
            mobileLinkUrl={primaryButtonMobileLink}
            trackingEvent={trackingEvent}
            trackingEventKey={trackingEventKey}
            trackingEventValue={trackingEventValue}
            hasScrollArrow={hasScrollArrow}
          />
        ) : null}
      </>
    )
  }

  useEffect(() => {
    setHeroCTAVisible(isCtaVisible)

    return () => {
      setHeroCTAVisible(false)
    }
  }, [isCtaVisible, setHeroCTAVisible])

  useEffect(() => {
    if (isPurchasable) {
      setHeroCTAPurchasableItem(purchasableItem)
    } else {
      setHeroCTAPurchasableItem(null)
    }

    return () => {
      setHeroCTAPurchasableItem(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPurchasable, purchasableItem])

  startObserving()

  return (
    <HeroLayout
      primaryButtonText={primaryButtonText}
      primaryButtonLink={primaryButtonLink}
      primaryButtonMobileLink={primaryButtonMobileLink}
      primaryButtonColor={primaryButtonBorderColor}
      trackingEvent={trackingEvent}
      trackingEventKey={trackingEventKey}
      trackingEventValue={trackingEventValue}
    >
      <div
        className={classNames(
          getBackgroundColorClass(backgroundColor),
          "pb-lg -mb-lg grid grid-cols-2 items-center"
        )}
        {...props}
      >
        <Section
          className={classNames(
            "col-span-full flex flex-col gap-16 pb-24 pt-24 sm:col-start-1 sm:col-end-2 sm:pb-72 md:gap-32 md:pt-navHeight-md lg:gap-40 lg:pt-navHeight-lg"
          )}
        >
          <div
            className={classNames(
              "flex flex-col gap-24",
              headingMobileAlignment === "left"
                ? "items-start"
                : "items-center text-center"
            )}
          >
            {callout && (
              <Callout
                color={getAccentColor(backgroundColor)}
                text={callout.text}
                highlightedWord={callout.highlightedWord}
                highlightColor={callout.highlightColor}
                hasBorder={callout.hasBorder}
                hasIcon={callout.hasIcon}
                ctaText={callout.ctaText}
                ctaLink={
                  callout.ctaLink && getUrlFromStoryblokLink(callout.ctaLink)
                }
              />
            )}
            <Typography
              text={headingText}
              font="grotesk"
              weight="medium"
              size={headingSize}
              color={accentColor}
              element="h1"
            />
            <Typography
              text={bodyText}
              font="grotesk"
              weight="book"
              size="body-lg"
              color={accentColor}
            />
          </div>
          <NumberDisplay
            type="price"
            size="large"
            adornmentTextPosition="left"
            adornmentText="R"
            textColor={accentColor}
            value={priceValue}
            sideScriptText={priceSidescriptText}
            subscriptText={priceSubscriptText}
            strikeSuperscriptText={true}
            superscriptText={priceSuperscriptText}
          />
          <div
            ref={textContainerRef}
            className="flex flex-col items-center gap-24 sm:flex-row sm:items-baseline"
          >
            {renderPrimaryCTA()}
            <TextButton
              style="text"
              text={secondaryButtonText}
              linkUrl={secondaryButtonLink}
              color={accentColor}
              trackingEvent={secondaryTrackingEvent}
              trackingEventKey={secondaryTrackingEventKey}
              trackingEventValue={secondaryTrackingEventValue}
            />
          </div>
          {iconUrl && (
            <GatsbyStoryblokImage image={iconUrl} alt={imageAlt} width={400} />
          )}
        </Section>
        <div
          className={classNames(
            "col-span-full mb-0 mt-40 sm:col-start-2 sm:col-end-3 sm:my-80 md:my-104",
            hasAnchorNavigation ? "lg:my-20" : "lg:my-72"
          )}
        >
          {videoUrl ? (
            <Video
              video={videoUrl}
              coverImage={imageUrl}
              ImageAlt={imageAlt || "Hero image"}
              loadType="eager"
              playbackMode="autoplay"
              aspectRatio="1:1"
            />
          ) : (
            <GatsbyStoryblokImage
              loadType="eager"
              className={classNames({
                "m-0 lg:m-24 xl:m-64": hasAnchorNavigation,
              })}
              image={imageUrl}
              alt={imageAlt || "Hero image"}
              quality={80}
            />
          )}
        </div>
      </div>
    </HeroLayout>
  )
}
export default HalfHero
